import {
  MMLElementType,
  ValidationResult,
  ValidationError,
  MMLElement,
  CommonAttributes,
  ModelAttributes,
  AudioAttributes,
  LabelAttributes,
  ImageAttributes,
  CylinderAttributes,
  LightAttributes,
  PlaneAttributes,
  VideoAttributes,
} from './types';

const FLOAT_ATTRIBUTES = [
  'x',
  'y',
  'z',
  'rx',
  'ry',
  'rz',
  'sx',
  'sy',
  'sz',
  'start-time',
  'font-size',
  'width',
  'height',
];
const BOOLEAN_ATTRIBUTES = ['loop'];

const COMMON_ATTRIBUTES = [
  'id',
  'x',
  'y',
  'z',
  'rx',
  'ry',
  'rz',
  'sx',
  'sy',
  'sz',
  'color',
  'class',
];

const ELEMENT_SPECIFIC_ATTRIBUTES = {
  [MMLElementType.CUBE]: [],
  [MMLElementType.SPHERE]: [],
  [MMLElementType.MODEL]: ['src'],
  [MMLElementType.GROUP]: [],
  [MMLElementType.AUDIO]: ['src', 'start-time', 'loop'],
  [MMLElementType.LABEL]: [
    'content',
    'font-size',
    'width',
    'height',
    'alignment',
    'font-color',
    'onclick',
  ],
  [MMLElementType.CYLINDER]: [],
  [MMLElementType.LIGHT]: ['type', 'intensity'],
  [MMLElementType.PLANE]: ['width', 'height'],
  [MMLElementType.IMAGE]: ['src', 'width', 'height'],
  [MMLElementType.VIDEO]: ['src', 'width', 'height', 'start-time', 'loop'],
};

const VALID_ATTRIBUTES: Record<MMLElementType, string[]> = {} as Record<
  MMLElementType,
  string[]
>;
Object.values(MMLElementType).forEach((type) => {
  VALID_ATTRIBUTES[type] = [
    ...COMMON_ATTRIBUTES,
    ...ELEMENT_SPECIFIC_ATTRIBUTES[type],
  ];
});

const REQUIRED_ATTRIBUTES: Record<MMLElementType, string[]> = {
  [MMLElementType.CUBE]: [],
  [MMLElementType.SPHERE]: [],
  [MMLElementType.MODEL]: ['src'],
  [MMLElementType.GROUP]: [],
  [MMLElementType.AUDIO]: ['src'],
  [MMLElementType.LABEL]: ['content'],
  [MMLElementType.CYLINDER]: [],
  [MMLElementType.LIGHT]: ['type'],
  [MMLElementType.PLANE]: [],
  [MMLElementType.IMAGE]: ['src'],
  [MMLElementType.VIDEO]: ['src'],
};

export function validateMML(mmlCode: string): ValidationResult {
  const errors: ValidationError[] = [];
  let parsedElements: MMLElement[] = [];

  if (!mmlCode || mmlCode.trim() === '') {
    return {
      isValid: false,
      errors: [
        {
          line: 1,
          column: 1,
          message: 'MML code cannot be empty',
          type: 'error',
        },
      ],
    };
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<root>${mmlCode}</root>`, 'text/xml');

    const parserError = doc.querySelector('parsererror');
    if (parserError) {
      const errorMessage = parserError.textContent || 'XML parsing error';
      const match = errorMessage.match(/line (\d+) at column (\d+)/);

      return {
        isValid: false,
        errors: [
          {
            line: match ? parseInt(match[1]) : 1,
            column: match ? parseInt(match[2]) : 1,
            message: errorMessage,
            type: 'error',
          },
        ],
      };
    }

    const rootElement = doc.documentElement;
    parsedElements = processElements(rootElement, errors);

    return {
      isValid: errors.length === 0,
      errors,
      parsedElements,
    };
  } catch (error: any) {
    return {
      isValid: false,
      errors: [
        {
          line: 1,
          column: 1,
          message: `Failed to parse MML: ${error.message || 'Unknown error'}`,
          type: 'error',
        },
      ],
    };
  }
}

function processElements(
  parentNode: Element,
  errors: ValidationError[],
  parentElement?: MMLElement
): MMLElement[] {
  const elements: MMLElement[] = [];

  for (let i = 0; i < parentNode.childNodes.length; i++) {
    const childNode = parentNode.childNodes[i];
    if (childNode.nodeType === Node.TEXT_NODE) {
      const text = childNode.textContent || '';
      if (text.trim() !== '') {
        errors.push({
          line: 1,
          column: 1,
          message: `Unexpected text outside of elements: "${text.trim()}"`,
          type: 'error',
        });
      }
    }
  }

  for (let i = 0; i < parentNode.children.length; i++) {
    const node = parentNode.children[i];
    const nodeName = node.nodeName.toLowerCase();

    const isValidType = Object.values(MMLElementType).includes(
      nodeName as MMLElementType
    );

    if (!isValidType) {
      errors.push({
        line: getLineNumber(node),
        column: getColumnNumber(node),
        message: `Unknown element type: ${nodeName}`,
        type: 'error',
        element: nodeName,
      });
      continue;
    }

    const elementType = nodeName as MMLElementType;
    const attributes: Record<string, string | number | boolean> = {};

    for (let j = 0; j < node.attributes.length; j++) {
      const attr = node.attributes[j];
      const attrName = attr.name;
      const attrValue = attr.value;

      if (!VALID_ATTRIBUTES[elementType].includes(attrName)) {
        errors.push({
          line: getLineNumber(node),
          column: getColumnNumber(node),
          message: `Invalid attribute '${attrName}' for element type '${elementType}'`,
          type: 'error',
          element: nodeName,
          attribute: attrName,
        });
        continue;
      }

      if (FLOAT_ATTRIBUTES.includes(attrName)) {
        attributes[attrName] = parseFloat(attrValue);
      } else if (BOOLEAN_ATTRIBUTES.includes(attrName)) {
        attributes[attrName] = attrValue === 'true';
      } else {
        attributes[attrName] = attrValue;
      }
    }

    for (const requiredAttr of REQUIRED_ATTRIBUTES[elementType]) {
      if (!(requiredAttr in attributes)) {
        errors.push({
          line: getLineNumber(node),
          column: getColumnNumber(node),
          message: `Missing required attribute '${requiredAttr}' for element type '${elementType}'`,
          type: 'error',
          element: nodeName,
        });
      }
    }

    for (let j = 0; j < node.childNodes.length; j++) {
      const childNode = node.childNodes[j];
      if (childNode.nodeType === Node.TEXT_NODE) {
        const text = childNode.textContent || '';
        if (text.trim() !== '') {
          errors.push({
            line: getLineNumber(node),
            column: getColumnNumber(node),
            message: `Unexpected text inside '${nodeName}' element: "${text.trim()}"`,
            type: 'error',
            element: nodeName,
          });
        }
      }
    }

    const element: MMLElement = {
      type: elementType,
      attributes,
      children: [],
      parent: parentElement,
      line: getLineNumber(node),
      column: getColumnNumber(node),
      rawContent: node.outerHTML,
    };

    validateElementAttributes(elementType, attributes, errors, node);

    if (node.children.length > 0) {
      element.children = processElements(node, errors, element);
    }

    elements.push(element);
  }

  return elements;
}

function getLineNumber(node: Element): number {
  return 1;
}

function getColumnNumber(node: Element): number {
  return 1;
}

export function getLineAndColumnInfo(
  mmlCode: string,
  elementName: string,
  index: number
): { line: number; column: number } {
  const lines = mmlCode.substring(0, index).split('\n');
  const line = lines.length;
  const column = lines[lines.length - 1].length + 1;

  return { line, column };
}

function isValidColor(color: string): boolean {
  if (/^#([0-9A-F]{3}){1,2}$/i.test(color)) {
    return true;
  }

  try {
    const s = new Option().style;
    s.color = color;
    return s.color !== '';
  } catch (e) {
    return false;
  }
}

function validateCommonAttributes(
  attributes: Record<string, string | number | boolean>,
  errors: ValidationError[],
  node: Element,
  elementType: MMLElementType
): void {
  const attrs = attributes as unknown as CommonAttributes;

  ['x', 'y', 'z', 'rx', 'ry', 'rz'].forEach((attr) => {
    if (
      attr in attrs &&
      (isNaN(Number(attrs[attr])) || !isFinite(Number(attrs[attr])))
    ) {
      errors.push({
        line: getLineNumber(node),
        column: getColumnNumber(node),
        message: `${elementType} ${attr} attribute must be a valid number`,
        type: 'error',
        element: elementType,
        attribute: attr,
      });
    }
  });

  ['sx', 'sy', 'sz'].forEach((attr) => {
    const value = attrs[attr];
    if (value !== undefined && (typeof value !== 'number' || value <= 0)) {
      errors.push({
        line: getLineNumber(node),
        column: getColumnNumber(node),
        message: `${elementType} ${attr} attribute must be a positive number`,
        type: 'error',
        element: elementType,
        attribute: attr,
      });
    }
  });

  if (attrs.color !== undefined && !isValidColor(String(attrs.color))) {
    errors.push({
      line: getLineNumber(node),
      column: getColumnNumber(node),
      message: `Invalid color format: ${attrs.color}`,
      type: 'error',
      element: elementType,
      attribute: 'color',
    });
  }
}

function validateElementAttributes(
  elementType: MMLElementType,
  attributes: Record<string, string | number | boolean>,
  errors: ValidationError[],
  node: Element
): void {
  validateCommonAttributes(attributes, errors, node, elementType);

  const stringAttrsToCheck = ['src', 'content'];

  stringAttrsToCheck.forEach((attr) => {
    if (
      attr in attributes &&
      typeof attributes[attr] === 'string' &&
      (attributes[attr] as string).trim() === ''
    ) {
      errors.push({
        line: getLineNumber(node),
        column: getColumnNumber(node),
        message: `${elementType} ${attr} attribute cannot be empty`,
        type: 'error',
        element: elementType,
        attribute: attr,
      });
    }
  });
}
